import { Organisation, UserRole } from '@avanzu/types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { getOrganisationProfile, updateOrganisationProfile } from '../../services';

export const OrganisationRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN];

// Month options for tax year start
const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

interface OrganisationFormData {
  name: string;
  legalName: string;
  abn: string;
  contactNumber: string;
  website: string;
  billingEmail: string;
  billingAddress: string;
  defaultRebatePercentage: number;
  taxYearStartMonth: number;
}

// Validation schema
const schema = yup.object({
  name: yup.string().required('Organisation name is required'),
  legalName: yup.string().default(''),
  abn: yup
    .string()
    .default('')
    .test('abn-format', 'ABN must be 11 digits', function (value: string | undefined) {
      if (!value || !value.trim()) return true; // Optional field
      const cleanABN = value.replace(/\s/g, '');
      return /^\d{11}$/.test(cleanABN);
    }),
  contactNumber: yup.string().default(''),
  website: yup
    .string()
    .default('')
    .test(
      'url-format',
      'Website must start with http:// or https://',
      function (value: string | undefined) {
        if (!value || !value.trim()) return true; // Optional field
        return /^https?:\/\/.+/.test(value);
      }
    ),
  billingEmail: yup
    .string()
    .required('Billing email is required')
    .email('Please enter a valid email'),
  billingAddress: yup.string().default(''),
  defaultRebatePercentage: yup
    .number()
    .required('Default rebate percentage is required')
    .min(0, 'Percentage must be at least 0')
    .max(100, 'Percentage cannot exceed 100'),
  taxYearStartMonth: yup
    .number()
    .required('Tax year start month is required')
    .min(1, 'Invalid month')
    .max(12, 'Invalid month'),
});

const OrganisationComponent: React.FC = () => {
  const { organisationId, refreshUserOrganisationState } = useAuth();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const [originalData, setOriginalData] = useState<Organisation | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<OrganisationFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      legalName: '',
      abn: '',
      contactNumber: '',
      website: '',
      billingEmail: '',
      billingAddress: '',
      defaultRebatePercentage: 43.5,
      taxYearStartMonth: 7, // Default to July (Australian financial year)
    },
  });

  // Watch all form values to detect changes
  watch(); // const formData = watch();

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  const fetchOrganisationData: () => Promise<Organisation | void> = async () => {
    setLoading(true);
    try {
      if (!organisationId) {
        navigate('/onboarding/organisation', { replace: true });
        return;
      }
      const organisation = await getOrganisationProfile(organisationId);
      if (!organisation) {
        navigate('/onboarding/organisation', { replace: true });
        return;
      }

      setOriginalData(organisation);

      // Set form values with formatted data
      setValue('name', organisation.name || '');
      setValue('legalName', organisation.legalName || '');
      setValue('abn', organisation.abn ? formatABN(organisation.abn) : '');
      setValue('contactNumber', organisation.contactNumber || '');
      setValue('website', organisation.website || '');
      setValue('billingEmail', organisation.billingEmail || '');
      setValue('billingAddress', organisation.billingAddress || '');
      setValue(
        'defaultRebatePercentage',
        organisation.defaultRebatePercentage ? organisation.defaultRebatePercentage * 100 : 43.5
      );
      setValue('taxYearStartMonth', organisation.taxYearStartMonth || 7);

      // Reset form dirty state after setting initial values
      reset({
        name: organisation.name || '',
        legalName: organisation.legalName || '',
        abn: organisation.abn ? formatABN(organisation.abn) : '',
        contactNumber: organisation.contactNumber || '',
        website: organisation.website || '',
        billingEmail: organisation.billingEmail || '',
        billingAddress: organisation.billingAddress || '',
        defaultRebatePercentage: organisation.defaultRebatePercentage
          ? organisation.defaultRebatePercentage * 100
          : 43.5,
        taxYearStartMonth: organisation.taxYearStartMonth || 7,
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('Failed to load organisation details', { variant: 'error' });
    }
  };

  const onSubmit = async (formData: OrganisationFormData) => {
    if (!organisationId) {
      enqueueSnackbar('Organisation ID is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Clean up the ABN by removing spaces for storage and prepare update payload
      const updatePayload = {
        ...formData,
        abn: formData.abn.replace(/\s/g, ''),
        website: formData.website || undefined,
        defaultRebatePercentage: formData.defaultRebatePercentage / 100,
      };

      const result = await updateOrganisationProfile(organisationId, updatePayload);
      refreshUserOrganisationState();

      // Update original data with the response
      if (result.organisation) {
        setOriginalData(result.organisation);

        // Reset form with new data to clear dirty state
        reset({
          name: result.organisation.name || '',
          legalName: result.organisation.legalName || '',
          abn: result.organisation.abn ? formatABN(result.organisation.abn) : '',
          contactNumber: result.organisation.contactNumber || '',
          website: result.organisation.website || '',
          billingEmail: result.organisation.billingEmail || '',
          billingAddress: result.organisation.billingAddress || '',
          defaultRebatePercentage: result.organisation.defaultRebatePercentage || 43.5,
          taxYearStartMonth: result.organisation.taxYearStartMonth || 7,
        });
      }

      setSaving(false);
      enqueueSnackbar('Organisation details updated successfully!', { variant: 'success' });
    } catch (error) {
      setSaving(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update organisation details';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Error saving organisation:', error);
    }
  };

  const formatABN = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX XXX XXX XXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar('Organisation ID copied to clipboard', { variant: 'info' });
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCancel = () => {
    if (originalData) {
      // Reset form to original data
      reset({
        name: originalData.name || '',
        legalName: originalData.legalName || '',
        abn: originalData.abn ? formatABN(originalData.abn) : '',
        contactNumber: originalData.contactNumber || '',
        website: originalData.website || '',
        billingEmail: originalData.billingEmail || '',
        billingAddress: originalData.billingAddress || '',
        defaultRebatePercentage: originalData.defaultRebatePercentage || 43.5,
        taxYearStartMonth: originalData.taxYearStartMonth || 7,
      });
    }
  };

  if (loading) {
    return (
      <ContentWrapper breadcrumb>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </ContentWrapper>
    );
  }

  if (!originalData) {
    return (
      <ContentWrapper breadcrumb>
        <Alert severity="error">Failed to load organisation data</Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper breadcrumb>
      <PaperSection
        title="Organisation Details"
        action={
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatDate(originalData.updatedAt)}
          </Typography>
        }
      >
        {/* Header */}
        <Box mb={4}>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Organisation ID:
              </Typography>
              <Chip
                label={originalData.id}
                size="small"
                variant="outlined"
                onDelete={undefined}
                sx={{ backgroundColor: '#fff' }}
                deleteIcon={
                  <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
                    <IconButton size="small" onClick={() => copyToClipboard(originalData.id || '')}>
                      {copied ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <CopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                }
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Plan:
              </Typography>
              <Chip
                label={originalData.subscriptionStatus}
                size="small"
                color={originalData.subscriptionStatus === 'ACTIVE' ? 'success' : 'default'}
              />
              {originalData.subscriptionValidUntil && (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    Valid Until:
                  </Typography>
                  <Typography variant="caption" color="text.primary">
                    {formatDate(originalData.subscriptionValidUntil)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={4} maxWidth={600}>
            {/* Company Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Company Information
              </Typography>
              <Stack spacing={3}>
                <Controller
                  name="name"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'name'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Organisation Name"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />

                <Controller
                  name="legalName"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'legalName'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Legal Name"
                      variant="outlined"
                      error={!!errors.legalName}
                      helperText={errors.legalName?.message || 'As registered with ASIC'}
                    />
                  )}
                />

                <Controller
                  name="abn"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'abn'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="ABN"
                      variant="outlined"
                      error={!!errors.abn}
                      helperText={errors.abn?.message || 'Australian Business Number (11 digits)'}
                      inputProps={{ maxLength: 14 }} // 11 digits + 3 spaces
                      onChange={(e) => {
                        const formatted = formatABN(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />

                <Controller
                  name="website"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'website'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Website"
                      variant="outlined"
                      placeholder="https://example.com.au"
                      error={!!errors.website}
                      helperText={errors.website?.message}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Financial Account Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Financial Account Information
              </Typography>
              <Stack spacing={3}>
                <Controller
                  name="defaultRebatePercentage"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'defaultRebatePercentage'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Default Rebate Percentage"
                      variant="outlined"
                      error={!!errors.defaultRebatePercentage}
                      helperText={
                        errors.defaultRebatePercentage?.message ||
                        'The default R&D tax incentive rebate percentage (typically 43.5% for most businesses)'
                      }
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  )}
                />

                <Controller
                  name="taxYearStartMonth"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'taxYearStartMonth'>;
                  }) => (
                    <FormControl fullWidth variant="outlined" error={!!errors.taxYearStartMonth}>
                      <InputLabel id="tax-year-start-month-label">Tax Year Start Month</InputLabel>
                      <Select
                        {...field}
                        labelId="tax-year-start-month-label"
                        label="Tax Year Start Month"
                      >
                        {MONTH_OPTIONS.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.taxYearStartMonth && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.taxYearStartMonth.message}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        The month your financial year begins (typically July for Australian
                        businesses)
                      </Typography>
                    </FormControl>
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Contact Information
              </Typography>
              <Stack spacing={3}>
                <Controller
                  name="contactNumber"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'contactNumber'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      error={!!errors.contactNumber}
                      helperText={errors.contactNumber?.message}
                    />
                  )}
                />

                <Controller
                  name="billingEmail"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'billingEmail'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Billing Email"
                      type="email"
                      variant="outlined"
                      error={!!errors.billingEmail}
                      helperText={
                        errors.billingEmail?.message ||
                        "Where we'll send invoices and billing notifications"
                      }
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Billing Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Billing Information
              </Typography>
              <Controller
                name="billingAddress"
                control={control}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<OrganisationFormData, 'billingAddress'>;
                }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Billing Address"
                    variant="outlined"
                    error={!!errors.billingAddress}
                    helperText={
                      errors.billingAddress?.message || 'Your registered business address'
                    }
                  />
                )}
              />
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {isDirty && (
                <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || !isDirty}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </PaperSection>
    </ContentWrapper>
  );
};

export default OrganisationComponent;
